import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UpgradeIcon from "@mui/icons-material/Upgrade";

import ComboBox from "lib/ui/textfield/ComboBox";

import { asTitle } from "utils/Utils";
import { AddEvent, ChangeEvent, DeleteEvent, Event } from "utils/history/Event";
import {
	BlueprintInfo,
	BlueprintType,
	getBlueprintsByType
} from "../base/BlueprintService";
import { getBlueprintLink } from "../base/Utils";
import { getReportBlueprintContent } from "../report/ReportBlueprintService";
import { Report } from "./TrajectoryBlueprintService";

export type ReportOverviewProps = {
	reports: Report[];
	selectedReport: number | undefined;
	enabled: boolean;

	onSelect: (index: number | undefined) => void;
	onChange: (event: Event) => void;
};

const ReportOverview: React.FC<ReportOverviewProps> = (
	props: ReportOverviewProps
) => {
	const { t } = useTranslation();

	const [name, setName] = useState("");
	const [newReportId, setNewReportId] = useState<number>();

	const [blueprints, setBlueprints] = useState<BlueprintInfo[]>([]);

	useEffect(() => {
		getBlueprintsByType(BlueprintType.REPORT).then(data => {
			data.sort((a, b) => a.name.localeCompare(b.name));
			setBlueprints(data);
		});
	}, []);

	function isValidName(): boolean {
		const cleanName = name.trim();
		const uniqueName =
			props.reports.find(r => r.name === cleanName) === undefined;
		return cleanName.length > 0 && uniqueName;
	}

	function onInsert(): void {
		const blueprint = blueprints.find(bp => bp.id === newReportId);
		if (blueprint === undefined) {
			return;
		}

		getReportBlueprintContent(blueprint.id).then(data => {
			const event = new AddEvent("/" + props.reports.length, {
				id: blueprint.id,
				version: blueprint.version,
				name: name.trim(),
				fields: data.fields
			});
			props.onChange(event);
			setName("");
			setNewReportId(undefined);
		});
	}

	function onDelete(): void {
		if (props.selectedReport === undefined) {
			return;
		}

		const event = new DeleteEvent(
			"/" + props.selectedReport,
			props.reports[props.selectedReport]
		);

		props.onSelect(undefined);
		props.onChange(event);
	}

	function linkedReportText(): JSX.Element {
		const label = t("linkedBlueprint", {
			document: "report"
		});

		if (props.selectedReport === undefined) {
			return <></>;
		}

		const selectedReport = props.reports[props.selectedReport];
		if (selectedReport !== undefined && selectedReport.id !== 0) {
			const bpId = selectedReport.id;
			const bpVersion = selectedReport.version;
			const blueprint = blueprints.find(bp => bp.id === bpId);

			if (blueprint === undefined) {
				return (
					<Typography variant="subtitle2">
						{label + ": " + t("removed")};
					</Typography>
				);
			} else {
				return (
					<Grid container>
						<Grid item container columnSpacing={1}>
							<Grid item>
								<Typography variant="subtitle2">
									{label}:
								</Typography>
							</Grid>
							<Grid item>
								<Typography variant="subtitle2">
									<Link
										to={getBlueprintLink(
											bpId,
											BlueprintType.REPORT
										)}>
										{blueprint.name}
									</Link>
								</Typography>
							</Grid>
						</Grid>
						<Grid item container alignItems="center">
							<Grid item>
								<Typography variant="subtitle2">
									{t("version")}: {bpVersion}/
									{blueprint.version}
								</Typography>
							</Grid>
							<Grid item>
								<IconButton
									size="small"
									disabled={
										!props.enabled ||
										bpVersion === blueprint.version
									}
									onClick={() =>
										updateLinkedReportBlueprint()
									}>
									<UpgradeIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Grid>
				);
			}
		}

		return (
			<Typography variant="subtitle2">
				{label + ": " + t("none")}
			</Typography>
		);
	}

	function updateLinkedReportBlueprint(): void {
		if (props.selectedReport === undefined) {
			return;
		}

		const reportId = props.selectedReport;
		const reportBlueprint = props.reports[reportId];
		const blueprint = blueprints.find(bp => bp.id === reportBlueprint.id);
		if (blueprint === undefined) {
			return;
		}

		getReportBlueprintContent(blueprint.id).then(data => {
			const event = new ChangeEvent(
				"/" + reportId,
				props.reports[reportId],
				{
					id: blueprint.id,
					version: blueprint.version,
					name: reportBlueprint.name,
					fields: data.fields
				}
			);
			props.onChange(event);
		});
	}

	const handleSelectReport = useCallback(
		(report: Report | null): void => {
			if (report === null) {
				props.onSelect(undefined);
				return;
			}

			const index = props.reports.findIndex(r => r.name === report.name);
			props.onSelect(index);
		},
		[props.reports, props.onSelect]
	);

	const handleChangeNewReportBlueprint = useCallback(
		(blueprint: BlueprintInfo | null): void => {
			setNewReportId(blueprint?.id);
		},
		[]
	);

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item container>
				<Grid item>
					<Typography variant="subtitle2">
						{asTitle(t("availableReportsInTrajectory"))}:
					</Typography>
				</Grid>
				<Grid item container alignItems="flex-end" spacing={2}>
					<Grid item xs={11} sm={5} md={3} lg={2}>
						<ComboBox
							label={t("report")}
							options={props.reports}
							value={
								props.selectedReport !== undefined
									? props.reports[props.selectedReport]
									: null
							}
							getOptionLabel={report => report.name}
							getOptionKey={report => report.name}
							onChange={handleSelectReport}
						/>
					</Grid>
					<Grid item xs={1}>
						<IconButton
							disabled={
								!props.enabled ||
								props.selectedReport === undefined
							}
							onClick={onDelete}>
							<DeleteIcon />
						</IconButton>
					</Grid>
					<Grid item xs={12} sm={6}>
						{linkedReportText()}
					</Grid>
				</Grid>
			</Grid>

			<Grid item container>
				<Grid item>
					<Typography variant="subtitle2">
						{asTitle(t("addReportToTrajectory"))}:
					</Typography>
				</Grid>
				<Grid item container alignItems="flex-end" spacing={2}>
					<Grid item xs={12} sm={6} md={3} lg={2}>
						<ComboBox
							options={blueprints}
							value={
								blueprints.find(bp => bp.id === newReportId) ??
								null
							}
							getOptionLabel={blueprint => blueprint.name}
							getOptionKey={blueprint => blueprint.id}
							onChange={handleChangeNewReportBlueprint}
						/>
					</Grid>

					<Grid item xs={11} sm={5} md={3} lg={2}>
						<TextField
							variant="standard"
							fullWidth
							required
							label={asTitle(t("name"))}
							disabled={!props.enabled}
							value={name}
							onChange={event => setName(event.target.value)}
							error={name !== "" && !isValidName()}
						/>
					</Grid>
					<Grid item xs={1}>
						<IconButton
							disabled={
								!props.enabled ||
								!isValidName() ||
								newReportId === undefined
							}
							onClick={onInsert}>
							<AddIcon />
						</IconButton>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default ReportOverview;
